<script>
export default {
  name: 'ReturnSearch'
}
</script>

<script setup>
import { ref, reactive, onMounted, nextTick } from 'vue';

import Pagination from '@parts/Pagination.vue';
import ReturnNavigation from '@parts/ReturnNavigation.vue';
import ReturnGoodsList from '@parts/ReturnGoodsList.vue';
import { dateUtil } from '@functions/dateUtil.js';
import DatePicker from '@parts/DatePicker.vue';
import axios from 'axios';
import { apiError } from '@functions/apiError.js';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import { modalUtil } from '@functions/modalUtil.js';
import TimeLimitOverConfirmModal from '@parts/modal/TimeLimitOverConfirmModal.vue';
import AddToReturnBoxConfirmModal from '@parts/modal/AddToReturnBoxConfirmModal.vue';
import AddToReturnBoxCompleteModal from '@parts/modal/AddToReturnBoxCompleteModal.vue';
import SearchNumOverModal from '@parts/modal/SearchNumOverModal.vue'
import { url } from '@/config/urls';
import { message } from '@functions/message.js';

// 外部定義メソッド
const { comparisonDateString, checkDateFormat, examValidDate } = dateUtil();
const { switchFromCurrentModal, showModal } = modalUtil();

// 画面ID
const screenId = ref("ReturnSearch");

// エラーメッセージの状態管理
const errorMessages = ref([]);

// 定数定義
const pageSizeArray = [50, 100, 200];

// Prop定義
const props = defineProps({
  userDataUrl: {
    type: String,
    default: url.api.userData
  },
  goodsTypesApiUrl: { 
    type: String,
    default: url.api.goodsTypes
  },
  returnSearchApiUrl: {
    type: String,
    default: url.api.returnSearch
  },
  returnBoxCountApiUrl: {
    type: String,
    default: url.api.returnBox.count
  },
  returnBoxAddApiUrl: {
    type: String,
    default: url.api.returnBox.add
  },
  errorUrl: {
    type: String,
    default: url.error
  },
  loadingIcon: {
    type: String,
    default: url.static.loadingIcon
  }
});

// ユーザー情報
const user = reactive({
  isPermittedReturnRequest: null  // null: 未取得, true: 権限あり, false: 権限なし
});

// ユーザー情報取得APIの呼び出し
const getUserInfo = async () => {
  try {
    const response = await axios.get(props.userDataUrl);
    user.isPermittedReturnRequest = response.data.response.isPermittedReturnRequest;
  } catch (error) {
    apiError(error, errorMessages);
  }
}

// 商品一覧取得APIリクエストパラメータ
const condition = ref({
  words: null,
  startDate: null,
  endDate: null,
  includeReturnedGoods: false,
  goodsTypeCode: null,
  size: 50,
  page: 1
});

// フォーム入力値
const formInputs = ref({
  words: '',
  startDate: '',
  endDate: '',
  includeReturnedGoods: false,
  goodsTypeCode: ''
});

// エラー
const errors = ref({
  words: '',
  date: '',
  goodsTypeCode: ''
});

// ページネーション
const pagingParameter = ref({
  pageSize: 50,
  pageNum: 1
});
const totalPage = ref(1);

// 商品分類マスタデータ
const goodsTypes = ref([]);

// 検索結果
const goodsList = ref([]);
const totalNum = ref(0);

// ローディングフラグ
const isLoading = ref(false);

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
  isLoading.value = loading;
}

// エラー処理
const handleApiError = (error) => {
	apiError(error, errorMessages);
};


// モーダル用商品データ（グローバル状態として管理）
const operatingGoodsData = ref(null);
// モーダル用商品データ更新
const setOperatingGoodsData = (goods) => {
  operatingGoodsData.value = goods;
}

// 返品ボックス行数
const returnBoxTotalRecord = ref(0);
// 返品ボックス行数集計
const countReturnBox = () => {
  axios.get(props.returnBoxCountApiUrl)
    .then((response) => {
      returnBoxTotalRecord.value = parseInt(response.data.response.totalNum);
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品分類マスタデータ取得処理
const getGoodsTypes = () => {
  axios.get(props.goodsTypesApiUrl)
    .then((response) => {
      goodsTypes.value = response.data.response.goodsTypes
        .filter(item => item.code !== "02" && item.code !== "04");
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品一覧取得処理
const getGoodsList = () => {
  handleUpdateLoading(true);
  axios.get(props.returnSearchApiUrl, { params:condition.value })
    .then((response) => {
      if (response.data.response.goodsList) goodsList.value = response.data.response.goodsList;
      totalNum.value = parseInt(response.data.response.totalNum);
      pagingParameter.value.pageNum = parseInt(response.data.response.currentPage);
      totalPage.value = Math.ceil(totalNum.value / pagingParameter.value.pageSize);
      totalPage.value = totalPage.value ? totalPage.value : 1;
      if (totalNum.value > 40000) {
        goodsList.value = [];
        nextTick(() => { 
          showModal('searchNumOverModal');
        });
        return;
      }
    })
    .catch((error) => {
      apiError(error, errorMessages);
    })
    .finally(() => {
      handleUpdateLoading(false);
    });
}


// 検索キーワード入力欄Enterキー押下時の処理
const onKeyDownEnterWords = (event) => {
  // 検索時フォーカスアウト(仮想キーボードの非表示)
  event.target.blur();

  clickSearchBtn();
}

// ボタンクリック
const clickSearchBtn = () => {
  dateValidation();
  wordsValidation();
  goodsTypesValidation();
  if (Object.values(errors.value).some(value => value !== '')) {
    return;
  }
  setCondition();
  getGoodsList();
}

const clickClearBtn = () => {
  formInputs.value.words = '';
  // 期間の初期値設定（開始日：一か月前、終了日：前日）
  setStartDateAndEndDate();
  formInputs.value.includeReturnedGoods = false;
  formInputs.value.goodsTypeCode = '';
}

// ページネーション処理
const onChangePageNum = (page) => {
  if (!Number.isFinite(page)) {
    return;
  }
  condition.value.page = page;
  getGoodsList();

  pagingParameter.value.pageNum = page;

  window.scroll({
      top: 0,
      behavior: 'smooth'
  });
}

const onChangePageSize = (selected) => {
  condition.value.page = 1;
  condition.value.size = selected;
  getGoodsList();

  pagingParameter.value.pageNum = 1;
  pagingParameter.value.pageSize = selected;

}

// 検索条件のセット
const setCondition = () => {
  condition.value.startDate = formInputs.value.startDate.replaceAll('/', '-') || null;
  condition.value.endDate = formInputs.value.endDate.replaceAll('/', '-') || null;
  condition.value.words = formInputs.value.words || null;
  condition.value.includeReturnedGoods = formInputs.value.includeReturnedGoods;
  condition.value.goodsTypeCode = formInputs.value.goodsTypeCode || null;
  condition.value.size = pagingParameter.value.pageSize || 50;
  condition.value.page = 1;
  pagingParameter.value.pageNum = 1;
}

// 日付バリデーション
const dateValidation = () => {
  errors.value.date = '';
  // 開始日
  if (formInputs.value.startDate) {
    if (!checkDateFormat(formInputs.value.startDate)) {
      errors.value.date = message.error.validation.date.format;
    }else if(!examValidDate(formInputs.value.startDate)){
      errors.value.date = message.error.validation.date.validity;
    }
  }
  // 終了日
  if (formInputs.value.endDate){
    if (!checkDateFormat(formInputs.value.endDate)) {
      errors.value.date = message.error.validation.date.format;
    }else if(!examValidDate(formInputs.value.endDate)){
      errors.value.date = message.error.validation.date.validity;
    }
  }
  // 比較
  if (formInputs.value.startDate && formInputs.value.endDate && !errors.value.date) {
    if (comparisonDateString(formInputs.value.startDate, '>', formInputs.value.endDate)) {
      let errorMessage = message.error.validation.date.comparison;
      errorMessage = errorMessage.replace('{end}', '終了日');
      errorMessage = errorMessage.replace('{start}', '開始日');
      errors.value.date = errorMessage;
    }
  }
}

// 検索キーワードバリデーション
const wordsValidation = () => {
  const maxLength = 255;
  errors.value.words = '';
  if (formInputs.value.words.length > maxLength) {
    let errorMessage = message.error.validation.string.length;
    errorMessage = errorMessage.replace('{max}', maxLength);
    errors.value.words = errorMessage;
  }
}

// 商品分類バリデーション
const goodsTypesValidation = () => {
  errors.value.goodsTypeCode = '';
  if (!formInputs.value.goodsTypeCode) { return; }
  if (!goodsTypes.value.some(goodsType => goodsType.code === formInputs.value.goodsTypeCode)) {
    errors.value.goodsTypeCode = message.error.not_found;
  }
}

// 期間の初期値設定
const setStartDateAndEndDate = () => {
  const today = new Date();
  
  // 開始日：一か月前
  const startDate = new Date(today);
  startDate.setMonth(today.getMonth() - 1);
  const startYear = startDate.getFullYear();
  const startMonth = String(startDate.getMonth() + 1).padStart(2, '0');
  const startDay = String(startDate.getDate()).padStart(2, '0');
  const formatStartDate = `${startYear}/${startMonth}/${startDay}`;
  
  // 終了日：前日
  const endDate = new Date(today);
  endDate.setDate(today.getDate() - 1);
  const endYear = endDate.getFullYear();
  const endMonth = String(endDate.getMonth() + 1).padStart(2, '0');
  const endDay = String(endDate.getDate()).padStart(2, '0');
  const formatEndDate = `${endYear}/${endMonth}/${endDay}`;
  
  formInputs.value.startDate = formatStartDate;
  formInputs.value.endDate = formatEndDate;
}

// 返品ボックス数量更新
const updateReturnBoxQuantity = (salesDetailsLotId, quantity) => {
  const index = goodsList.value.findIndex(goods => goods.salesDetailsLotId === salesDetailsLotId);
  goodsList.value[index].returnBoxQuantity += quantity;
}

// 重複チェック
const checkDuplicateInReturnBox = () => {
  const salesDetailsLotId = operatingGoodsData.value.salesDetailsLotId;
  handleUpdateLoading(true);
  axios.get(props.returnBoxCountApiUrl, { params: { salesDetailsLotIdList: [salesDetailsLotId] } })
    .then((response) => {
      if (response.data.response.totalNum > 0) {
        // 重複ありのため、警告モーダルを表示
        handleUpdateLoading(false);
        switchFromCurrentModal('addToReturnBoxConfirmModal');
      } else {
        // 問題なしのため、追加処理を実施
        addToReturnBox();
      }
    })
    .catch((error) => {
      handleUpdateLoading(false);
      apiError(error, errorMessages);
    });
}

// 返品ボックス 追加
let isAddingToReturnBox = false;
const addToReturnBox = () => {
  if (isAddingToReturnBox) return;
  isAddingToReturnBox = true;

  const salesDetailsLotId = operatingGoodsData.value.salesDetailsLotId;
  const quantity = operatingGoodsData.value.quantity;

  const goods = {
    salesDetailsLotId: parseInt(salesDetailsLotId),
    quantity: parseInt(quantity)
  };
  handleUpdateLoading(true);
  axios.post(props.returnBoxAddApiUrl, {goodsList: [goods]})
    .then((response) => {
      switchFromCurrentModal('addToReturnBoxCompleteModal');
      updateReturnBoxQuantity(salesDetailsLotId, quantity);
      countReturnBox();
    })
    .catch((error) => {
      apiError(error, errorMessages);
    })
    .finally(() => {
      // 操作中商品データをクリア
      operatingGoodsData.value = null;
      handleUpdateLoading(false);
      isAddingToReturnBox = false;
    });
}

// 初期処理
onMounted(async () => {
  await getUserInfo();
  if (!user.isPermittedReturnRequest) {
    return;
  }

  // 期間の初期値設定（開始日：一か月前、終了日：前日）
  setStartDateAndEndDate();

  countReturnBox();
  getGoodsTypes();
});

</script>

<template>

  <div id="contents1" class="bg3" v-if="user.isPermittedReturnRequest">
    <ReturnNavigation
      :screenId = "screenId"
      :returnBoxTotalRecord = returnBoxTotalRecord
    ></ReturnNavigation>

    <h2 class="com_title1 com_mgb5">返品依頼</h2>

    <div class="com_result1">
      <div class="com_inner2">
      <!-- searchForm -->
        <div class="datepicker-group1">
          <div>
            <label>伝票日付</label>
            <div>
              <DatePicker
                v-model="formInputs.startDate"
                identifier="js-datepicker1" ref="startDate"
                :class="{ error: errors.date }"
              ></DatePicker>
            </div>
            <span>〜</span>
            <div>
              <DatePicker
                v-model="formInputs.endDate"
                identifier="js-datepicker2" ref="endDate"
                :class="{ error: errors.date }"
              ></DatePicker>
            </div>
          </div>
        </div>
        <div class="com_validate_msg1 st1 error" v-if="errors.date">{{ errors.date }}</div>
        <div class="group12">
          <div>
            <input type="text" placeholder="メーカー名、商品名、商品コード、GTIN"
                class="form-control com_form1" v-model="formInputs.words"
                :class="{ error: errors.words }"
                @keydown.enter="onKeyDownEnterWords" enterkeyhint="search">
            <div class="com_validate_msg1 error" v-if="errors.words">{{ errors.words }}</div>
          </div>
          <div>
            <div class="com_form_check1"><input type="checkbox" v-model="formInputs.includeReturnedGoods"><label>返品依頼済み商品を含む</label></div>
          </div>
        </div>
        <div class="group13">
          <select class="form-select com_form1" v-model="formInputs.goodsTypeCode" :class="{ error: errors.goodsTypeCode }">
            <option value="" selected>商品分類:すべて</option>
            <option v-for="goodsType in goodsTypes" :key="goodsType.code" :value="goodsType.code">
              {{ goodsType.name }}
            </option>
          </select>
          <button type="button" class="btn com_btn1" @click.prevent="clickSearchBtn">検索</button>
          <button type="button" class="btn com_btn2" @click.prevent="clickClearBtn">クリア</button>
        </div>
        <div style="display: flex;">
          <div class="com_validate_msg1 error" v-if="errors.goodsTypeCode">{{ errors.goodsTypeCode }}</div>
        </div>
      </div>
    </div>
    <div class="com_inner5 com_mgt30 clearfix">
      <template v-if="goodsList.length === 0">
        <p class="com_fs16 text-center">検索項目を入力してください。</p>
      </template>
      <template v-else>
        <p class="com_fs16 text-center order-results-text">検索結果は以下の通りです</p>

        <Pagination
          :isShowFirstAndLastBtn="true"
          :isShowPaginationInfo="true"
          :totalNum="totalNum"
          :currentPage="pagingParameter.pageNum"
          :totalPage="totalPage"
          :pageSize="pagingParameter.pageSize"
          :pageSizeSelectList="pageSizeArray"
          @changePageNum="onChangePageNum"
          @changePageSize="onChangePageSize"
        ></Pagination>

        <ReturnGoodsList
          :screenId = "screenId"
          :goodsList = goodsList
          :goodsTypes = goodsTypes
          @updateReturnBoxQuantity="updateReturnBoxQuantity"
          @updateLoading="handleUpdateLoading"
          @countReturnBox="countReturnBox"
          @setOperatingGoodsData="setOperatingGoodsData"
          @checkDuplicateInReturnBox="checkDuplicateInReturnBox"
          @addToReturnBox="addToReturnBox"
          @apiError="handleApiError"
        ></ReturnGoodsList>

        <Pagination
          :isShowFirstAndLastBtn="true"
          :isShowPaginationInfo="true"
          :totalNum="totalNum"
          :currentPage="pagingParameter.pageNum"
          :totalPage="totalPage"
          :pageSize="pagingParameter.pageSize"
          :pageSizeSelectList="pageSizeArray"
          @changePageNum="onChangePageNum"
          @changePageSize="onChangePageSize"
        ></Pagination>

      </template>
    </div>
    
  </div>
    
  <!-- 伝票日付3ヵ月経過警告モーダル -->
  <TimeLimitOverConfirmModal
    @timeLimitOverConfirmed="checkDuplicateInReturnBox"
  ></TimeLimitOverConfirmModal>

  <!-- 返品ボックスに追加確認モーダル -->
  <AddToReturnBoxConfirmModal
    @addToReturnBox="addToReturnBox"
  ></AddToReturnBoxConfirmModal>

  <!-- 返品ボックスに追加完了モーダル -->
  <AddToReturnBoxCompleteModal
    :screenId="screenId"
  ></AddToReturnBoxCompleteModal>

  <SearchNumOverModal
    :total-num="totalNum"
  ></SearchNumOverModal>

  <ApiErrorModal
    :messages="errorMessages"
  ></ApiErrorModal>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>

</template>